import * as Bowser from 'bowser';

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApAuthenticationService } from '@ap/browser-authenticator';
import { ApWindowService } from '@ap/cdk';
import { ApContentService, ApHeadlessContentService } from '@ap/content';
import { SessiebeheerResource } from '@ap/interface-apic-sessiebeheer';
import { ApLoggingService } from '@ap/logging';
import { ApPersistenceService } from '@ap/persistence';

import { NotificationText } from '../mijn-login.model';

import {
    BrowserSessionName,
    BrowserSessionType,
    HuismaprequestSession,
    InstantNotificationContentKey,
    MINIMAL_BROWSER_VERSIONS,
    RegularRequestSession
} from './login.wrapper.constants';
import content from './login.wrapper.content.json';
import { BrowserTokensSession } from './login.wrapper.model';

const ibpHomepageLink = 'mijn.login.login.wrapper.ibphomepage.link';
const mijnOmgevingLink = 'mijn.login.login.wrapper.mijn-omgeving.link';

@Injectable({
    providedIn: 'root'
})
export class LoginWrapperService {
    public constructor(
        private readonly apAuthenticationService: ApAuthenticationService,
        private readonly apContentService: ApContentService,
        private readonly apHeadlessContentService: ApHeadlessContentService,
        private readonly apLoggingService: ApLoggingService,
        private readonly apPersistenceService: ApPersistenceService,
        private readonly apWindowService: ApWindowService,
        private readonly sessiebeheerResource: SessiebeheerResource
    ) {
        apContentService.addContentItems(content);
    }

    private static mapHeadlessContentToNotificationText(headlessContent: { [key: string]: string }): NotificationText {
        if (
            headlessContent == null ||
            headlessContent[InstantNotificationContentKey] == null ||
            headlessContent[InstantNotificationContentKey].length === 0
        ) {
            return null;
        }
        return {
            [InstantNotificationContentKey]: headlessContent[InstantNotificationContentKey]
        };
    }

    public notificationText(): Observable<NotificationText> {
        return this.apHeadlessContentService.getContentItems('@ap/mijn-login-message').pipe(
            map((result) => LoginWrapperService.mapHeadlessContentToNotificationText(result)),
            catchError(() => of(null))
        );
    }

    public postSession(actionId: string, isHuismapLogin: boolean, finishLogin = false): Observable<void> {
        let session;
        if (!finishLogin) {
            session = isHuismapLogin ? HuismaprequestSession : RegularRequestSession;
            session.started_from_device_fingerprint = this.apAuthenticationService.getFingerprint();
        } else {
            session = { authenticated_with_authentication_task_id: actionId };
        }
        return this.sessiebeheerResource.postSession(session, {
            authenticationTaskIdHeaderParam: actionId
        });
    }

    public setBrowserTokensSession(keychainId: string): void {
        const browserTokensSession: BrowserTokensSession = {
            keychainId
        };
        this.apPersistenceService.setSession(BrowserSessionName, BrowserSessionType, browserTokensSession);
    }

    public calculateRedirectUrl(orgurl: string, orgurlFragment: string): string {
        let redirectUrl: string;
        if (orgurl != null && orgurl !== '') {
            const absoluteOrgurl = this.createAbsoluteURL(orgurl, orgurlFragment);
            if (absoluteOrgurl.endsWith(this.apContentService.get(ibpHomepageLink))) {
                redirectUrl = this.apContentService.get(mijnOmgevingLink);
            }
            if (!redirectUrl && this.canConvertToURL(absoluteOrgurl)) {
                if (!this.isValidUrl(absoluteOrgurl)) {
                    return this.apContentService.get('mijn.login.login.wrapper.mijn-omgeving.notfound.link');
                }
                redirectUrl = absoluteOrgurl;
            }
        }
        if (!redirectUrl) {
            redirectUrl = this.setDefaultRedirectURL();
        }
        return redirectUrl;
    }

    public browserIsUpToDate(): boolean {
        const browser = Bowser.getParser(window.navigator.userAgent);
        let upToDate = false;
        if (window.location.href.toString().includes('localhost')) {
            return true;
        }

        MINIMAL_BROWSER_VERSIONS.forEach((browserInstance) => {
            if (browser.isBrowser(browserInstance.name, true)) {
                if (browser.satisfies(browserInstance.version)) {
                    upToDate = true;
                }
            }
        });
        return upToDate;
    }

    private createAbsoluteURL(url: string, fragment: string): string {
        let absoluteUrl = url;

        if (fragment != null) {
            absoluteUrl = `${url}%23${fragment}`;
        }

        if (!absoluteUrl.includes('://')) {
            const location = this.apWindowService.getWindow().location;
            absoluteUrl = `${location.protocol}//${location.hostname}${location.port ? ':' : ''}${location.port}${absoluteUrl}`;
        }
        return absoluteUrl;
    }
    private setDefaultRedirectURL(): string {
        return this.apContentService.get(mijnOmgevingLink);
    }

    private canConvertToURL(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            this.apLoggingService.error('Invalid redirect, failed to construct URL');
            return false;
        }
    }

    private isValidUrl(url: string): boolean {
        if (url.includes('url.html')) {
            return false;
        }

        const regex = '^(http|https):\\/\\/(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(\\/|\\/([\\w#!:.?+=&%@!\\-\\/]))?$';

        if (url.match(regex)) {
            const fullUrl = new URL(url);
            const host: string = fullUrl.hostname;
            const path: string = fullUrl.pathname;
            const location = this.apWindowService.getWindow().location;

            if (
                !host.match('^$') &&
                path.search('userInfo') === -1 &&
                (host === location.hostname || host === location.host) &&
                url.length <= 255
            ) {
                return true;
            }

            this.apLoggingService.error('Invalid redirect, host not empty or userInfo not null');
            return false;
        }

        this.apLoggingService.error('Invalid redirect, regex did not match!');
        return false;
    }
}
